import { CONTRACT_STATUS_LABELS, type ContractStatusHistoryEntry, type PortalPhase } from '../api/contracts'
import { PortalPhaseDot } from './PortalPhaseDot'
import { Icon } from './ui'

/**
 * "Dove si trova la pratica": i 20 stati raggruppati in 5 tappe leggibili da chiunque, con la tappa attuale
 * evidenziata e una frase su chi deve fare cosa. Gli stati fuori percorso (sospesa, rifiutata, annullata,
 * stornata) fermano la pratica sulla tappa raggiunta, ricavata dallo storico degli stati.
 * Solo lettura: i cambi di stato restano quelli dichiarati dal backend nel riepilogo.
 */
const PHASES = [
  { label: 'Inserita', statuses: ['Bozza', 'InAttesaDiFirma'] },
  { label: 'Controllo backoffice', statuses: ['InviataAlBackoffice', 'DaVerificare', 'IntegrazioneRichiesta', 'Verificata', 'ApprovazioneRichiesta', 'Approvata'] },
  { label: 'Caricamento sul portale', statuses: ['InAttesaDiAssegnazioneScarico', 'AssegnataALotto', 'CaricamentoOperatoreInCorso', 'BozzaOperatoreCreata', 'DaConfermare'] },
  { label: "Presso l'operatore", statuses: ['InviataAllOperatore', 'InLavorazioneOperatore'] },
  { label: 'Attivata', statuses: ['Attivata'] },
] as const

const STOPPED: Record<string, { tone: 'warning' | 'error' | 'neutral'; text: string }> = {
  Sospesa: { tone: 'warning', text: 'La pratica è ferma: va corretta e poi ripresa.' },
  Rifiutata: { tone: 'error', text: "La pratica è stata rifiutata e non prosegue." },
  Annullata: { tone: 'neutral', text: 'La pratica è stata annullata e non prosegue.' },
  Stornata: { tone: 'error', text: 'Il contratto è stato stornato dopo l’attivazione.' },
}

const WHAT_NOW: Record<string, string> = {
  Bozza: 'Da completare e inviare al backoffice.',
  InAttesaDiFirma: 'Il cliente deve firmare con il codice che ha ricevuto.',
  InviataAlBackoffice: 'Il backoffice deve prenderla in carico.',
  DaVerificare: 'Il backoffice sta controllando dati e documenti.',
  IntegrazioneRichiesta: 'Il backoffice chiede di integrare dati o documenti.',
  Verificata: 'Controllata dal backoffice, pronta per il caricamento.',
  ApprovazioneRichiesta: 'Serve un’approvazione prima di proseguire.',
  Approvata: 'Approvata, pronta per il caricamento.',
  InAttesaDiAssegnazioneScarico: 'In attesa di essere assegnata a un caricamento.',
  AssegnataALotto: 'Assegnata a un lotto da caricare.',
  CaricamentoOperatoreInCorso: 'Il caricamento sul portale dell’operatore è in corso.',
  BozzaOperatoreCreata: 'La bozza è creata sul portale dell’operatore.',
  DaConfermare: 'Va confermata sul portale dell’operatore.',
  InviataAllOperatore: 'Inviata: si aspetta la risposta dell’operatore.',
  InLavorazioneOperatore: 'L’operatore la sta lavorando.',
  Attivata: 'Il contratto è attivo.',
}

function phaseOf(status: string): number {
  return PHASES.findIndex(p => (p.statuses as readonly string[]).includes(status))
}

/** `portalPhase`: fase vera della card sul portale; finché è operativa, il «!» accanto allo stato ne dà il dettaglio. */
export function ContractProgress({ status, history, portalPhase }: {
  status: string
  history: ContractStatusHistoryEntry[]
  portalPhase?: PortalPhase | null | undefined
}) {
  const sorted = [...history].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
  const stop = STOPPED[status]
  let current = phaseOf(status)
  if (current < 0) {
    // Fuori percorso: la tappa è l'ultima raggiunta prima dello stop (dallo storico), altrimenti la prima.
    const reached = sorted.map(h => phaseOf(h.fromStatus ?? '')).concat(sorted.map(h => phaseOf(h.toStatus)))
    current = Math.max(0, ...reached)
  }
  const since = [...sorted].reverse().find(h => h.toStatus === status)?.occurredAt
  const label = CONTRACT_STATUS_LABELS[status] ?? status
  const done = status === 'Attivata'

  return (
    <section className="ecrm-progress" data-tone={stop?.tone ?? (done ? 'success' : 'normal')} aria-label="Dove si trova la pratica">
      <div className="ecrm-progress__now">
        <span className="ecrm-progress__dot" aria-hidden="true" />
        <div>
          <p className="ecrm-progress__status">
            {label}
            <PortalPhaseDot phase={portalPhase} />
            {since ? <span className="ecrm-progress__since"> dal {new Date(since).toLocaleDateString('it-IT')}</span> : null}
          </p>
          <p className="ecrm-progress__what">{stop?.text ?? WHAT_NOW[status] ?? ''}</p>
        </div>
      </div>
      <ol className="ecrm-progress__track">
        {PHASES.map((p, i) => {
          const state = i < current || (done && i === current) ? 'done' : i === current ? (stop ? 'stopped' : 'current') : 'todo'
          return (
            <li key={p.label} className="ecrm-progress__step" data-state={state} aria-current={i === current ? 'step' : undefined}>
              <span className="ecrm-progress__mark" aria-hidden="true">
                {state === 'done' ? <Icon name="check" size={18} /> : state === 'stopped' ? <Icon name={stop?.tone === 'warning' ? 'pause' : 'close'} size={18} /> : i + 1}
              </span>
              <span className="ecrm-progress__label">{p.label}</span>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
